import { supabase } from '../lib/supabaseClient';

const DEMO_LOGIN_CREDENTIALS = Object.freeze({
  email: 'demo@cogito.com',
  password: 'demo123',
});

const DEMO_REGISTRATION_CREDENTIALS = Object.freeze({
  email: 'demo@cogito.com',
  password: 'Cogito123!',
});

const ensureProfileForUser = async ({ id, email, fullName }) => {
  if (!id) {
    return;
  }

  const profilePayload = {
    id,
    email,
    full_name: fullName?.trim?.() || email,
  };

  const { error } = await supabase
    .from('profiles')
    .upsert(profilePayload, { onConflict: 'id' });

  if (error) {
    throw error;
  }
};

const tryEnsureProfileForUser = async (args) => {
  try {
    await ensureProfileForUser(args);
  } catch (profileError) {
    console.warn('Failed to synchronize profile record:', profileError);
  }
};

export const getAuthProfile = async () => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) {
    throw error;
  }

  if (!user) {
    return null;
  }

  const { data: profileData, error: profileError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (profileError && profileError.code !== 'PGRST116') {
    throw profileError;
  }

  return {
    id: user.id,
    email: user.email,
    ...profileData,
  };
};

export const isAuthenticated = async () => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return Boolean(session);
};

export const login = async ({ email, password }) => {
  const isDemoLogin =
    email?.toLowerCase?.() === DEMO_LOGIN_CREDENTIALS.email && password === DEMO_LOGIN_CREDENTIALS.password;

  if (isDemoLogin) {
    const { data, error } = await supabase.auth.signInAnonymously();

    if (error) {
      throw error;
    }

    if (data?.user) {
      await tryEnsureProfileForUser({
        id: data.user.id,
        email: DEMO_LOGIN_CREDENTIALS.email,
        fullName: 'Cogito Demo User',
      });
    }

    return data;
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  if (data?.user) {
    await tryEnsureProfileForUser({
      id: data.user.id,
      email: data.user.email,
      fullName: data.user.user_metadata?.full_name,
    });
  }

  return data;
};

export const loginWithProvider = async (provider) => {
  const { data, error } = await supabase.auth.signInWithOAuth({ provider });

  if (error) {
    throw error;
  }

  return data;
};

export const register = async (data, { consents = {} } = {}) => {
  const { email, password, fullName } = data;
  const {
    data: signUpData,
    error: signUpError,
  } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: fullName,
      },
    },
  });

  if (signUpError) {
    throw signUpError;
  }

  if (signUpData?.user && signUpData?.session) {
    await tryEnsureProfileForUser({
      id: signUpData.user.id,
      email: signUpData.user.email,
      fullName,
    });
  }

  const confirmationRequired = Boolean(signUpData?.user) && !signUpData?.session;

  return {
    ...signUpData,
    emailConfirmationRequired: confirmationRequired,
  };
};

export const logout = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) {
    throw error;
  }
};

export const clearAuthData = logout;

export const onAuthStateChange = (callback) => supabase.auth.onAuthStateChange(callback);

export const resendConfirmationEmail = async (email) => {
  if (!email) {
    throw new Error('Email is required to resend confirmation.');
  }

  const { data, error } = await supabase.auth.resend({
    type: 'signup',
    email,
  });

  if (error) {
    throw error;
  }

  return data;
};

export const getDemoLoginCredentials = () => ({
  ...DEMO_LOGIN_CREDENTIALS,
});

export const getDemoRegistrationCredentials = () => ({
  ...DEMO_REGISTRATION_CREDENTIALS,
});
